import { useState } from 'react';

const EMAIL_CONFIG = {
  functionUrl: 'https://e6qonawcoyk7x2ronvvovrwmr40vaqjy.lambda-url.us-east-1.on.aws/',
  emailToken: '2a212fc8408b74a713a5a933ca32e773f5989793fd070d2e850eddbc2af96642',
  source: 'blushandbobby-website',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s\-()]{10,}$/;
const FALLBACK_EMAIL = 'noemailprovided@gmail.com';
const FALLBACK_PHONE = '111-111-1111';

const SERVICE_OPTIONS = ['Bridal', 'Special Event', 'Med Spa', 'Other'];

const INPUT_CLASSES =
  'w-full h-12 px-4 bg-charcoal-dark/60 border-2 border-dusty-rose/30 rounded text-chalk placeholder:text-chalk/40 focus:border-dusty-rose focus:outline-none focus:ring-1 focus:ring-dusty-rose transition-colors';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_interest: '',
    event_date: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const generateNonce = () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const email = formData.email.trim();
    const phone = formData.phone.trim();

    if (!email && !phone) {
      return 'Please provide either an email address or phone number.';
    }

    const hasValidEmail = !!email && EMAIL_REGEX.test(email);
    const hasValidPhone = !!phone && PHONE_REGEX.test(phone);

    if (!hasValidEmail && !hasValidPhone) {
      if (email && phone) return 'Please provide at least one valid contact method.';
      if (email) return 'Please enter a valid email address.';
      return 'Please enter a valid phone number (10+ digits).';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const timestamp = Date.now().toString();
      const nonce = generateNonce();

      const normalizedName = formData.name.trim();
      const nameParts = normalizedName ? normalizedName.split(/\s+/) : [];
      const first_name = nameParts[0] || 'No name provided';
      const last_name =
        nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'User did not provide input';

      const normalizedEmail = formData.email.trim();
      const normalizedPhone = formData.phone.trim();
      const email = EMAIL_REGEX.test(normalizedEmail) ? normalizedEmail : FALLBACK_EMAIL;
      const phone = PHONE_REGEX.test(normalizedPhone) ? normalizedPhone : FALLBACK_PHONE;
      const message = formData.message.trim() || 'User did not provide input';
      const service_interest = formData.service_interest.trim() || 'Not specified';
      const event_date = formData.event_date.trim() || 'Not specified';

      const requestData = {
        first_name,
        last_name,
        email,
        phone,
        message,
        service_interest,
        event_date,
      };

      const response = await fetch(EMAIL_CONFIG.functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-email-source': EMAIL_CONFIG.source,
          'x-email-token': EMAIL_CONFIG.emailToken,
          'x-email-timestamp': timestamp,
          'x-email-nonce': nonce,
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_interest: '',
          event_date: '',
          message: '',
        });
      } else {
        throw new Error(result.error || result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to send message. Please try again.'
      );
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="bg-black/70 border border-dusty-rose/30 rounded-2xl shadow-xl p-10 text-center">
        <div className="w-16 h-16 bg-dusty-rose rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-chalk"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="font-headline text-2xl uppercase tracking-wider text-chalk mb-3">
          Inquiry Received
        </h3>
        <p className="text-chalk/80 mb-8">
          Thanks for reaching out. Gabrielle will be in touch within one business day.
        </p>
        <button
          type="button"
          onClick={() => setSubmitStatus('idle')}
          className="border-2 border-dusty-rose text-dusty-rose hover:bg-dusty-rose hover:text-chalk px-6 py-3 rounded font-semibold uppercase tracking-wider text-sm transition-colors"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div className="bg-black/70 border border-dusty-rose/30 rounded-2xl shadow-xl p-8 md:p-10">
      <div className="mb-8 text-center">
        <h3 className="font-headline text-2xl md:text-3xl uppercase tracking-wider text-chalk mb-2">
          Inquire
        </h3>
        <p className="text-chalk/80">
          Tell us about your event and we&apos;ll follow up within one business day.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-chalk mb-2">
            Name
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Your name"
            className={INPUT_CLASSES}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-chalk mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="you@example.com"
              className={INPUT_CLASSES}
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-chalk mb-2">
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(555) 555-5555"
              className={INPUT_CLASSES}
            />
          </div>
        </div>

        <p className="text-xs text-chalk/60 -mt-2">Provide at least one — email or phone.</p>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="service_interest"
              className="block text-sm font-semibold text-chalk mb-2"
            >
              Service Interest
            </label>
            <select
              id="service_interest"
              value={formData.service_interest}
              onChange={(e) => handleChange('service_interest', e.target.value)}
              className={INPUT_CLASSES}
            >
              <option value="">Select one</option>
              {SERVICE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="event_date" className="block text-sm font-semibold text-chalk mb-2">
              Event Date
            </label>
            <input
              id="event_date"
              type="date"
              value={formData.event_date}
              onChange={(e) => handleChange('event_date', e.target.value)}
              className={INPUT_CLASSES}
            />
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-chalk mb-2">
            Tell us about your event
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            placeholder="Venue, party size, vision, timeline, anything else you'd like to share..."
            rows={5}
            className="w-full px-4 py-3 bg-charcoal-dark/60 border-2 border-dusty-rose/30 rounded text-chalk placeholder:text-chalk/40 focus:border-dusty-rose focus:outline-none focus:ring-1 focus:ring-dusty-rose transition-colors resize-none"
          />
        </div>

        {submitStatus === 'error' && (
          <div className="flex items-start gap-2 text-red-200 bg-red-900/40 border border-red-400/40 p-3 rounded">
            <svg
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-sm">{errorMessage}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-dusty-rose text-chalk hover:bg-dusty-rose/90 disabled:bg-gray-500 disabled:cursor-not-allowed py-4 rounded text-base font-semibold uppercase tracking-wider transition-colors"
        >
          {isSubmitting ? 'Sending...' : 'Send Inquiry'}
        </button>
      </form>
    </div>
  );
}
